// src/pages/StartPath.jsx
// "Choose Your Path" screen — shown to brand-new users after NewHome.
// Two clean white cards: tutorial flow vs jump straight to Level 1.

import React from "react";
import { useNavigate } from "react-router-dom";

export default function StartPath() {
  const navigate = useNavigate();

  return (
    <div style={ROOT}>
      <style>{CSS}</style>

      {/* Floating music notes */}
      <div aria-hidden style={NOTES_LAYER}>
        {["♪","♫","♩","♬","♪","♫"].map((n, i) => (
          <span key={i} className="sp-note" style={{
            left: `${8 + i * 16}%`,
            fontSize: 14 + (i % 3) * 5,
            animationDelay: `${i * 1.2}s`,
            animationDuration: `${8 + i * 0.8}s`,
          }}>{n}</span>
        ))}
      </div>

      <div style={INNER}>
        {/* Logo / brand */}
        <div style={BRAND}>
          <img
            src="/icons/pwp-192.png"
            alt="Piano With Pan"
            style={{ width: 52, height: 52, borderRadius: 14, objectFit: "contain" }}
            onError={e => e.currentTarget.style.display = "none"}
          />
          <div>
            <div style={BRAND_TITLE}>Piano With Pan</div>
            <div style={BRAND_SUB}>🐉 Treble Town Adventure</div>
          </div>
        </div>

        {/* Heading */}
        <h1 style={HEADING}>How do you want to start?</h1>
        <p style={SUB}>Pick the path that's right for you.</p>

        {/* Cards */}
        <div style={CARDS}>

          {/* Card A — Tutorial */}
          <button
            type="button"
            className="sp-card"
            onClick={() => navigate("/learn-your-notes")}
            style={CARD}
          >
            <div style={CARD_ICON}>🎓</div>
            <div style={CARD_LABEL}>I'm New Here</div>
            <div style={CARD_DESC}>
              Learn the notes first, then practice — the game will teach you everything step by step.
            </div>
            <div style={{ ...CARD_CTA, background: "#4f46e5", color: "#fff" }}>
              Start Tutorial →
            </div>
            <div style={CARD_STEPS}>
              Learn Notes · Practice Mode · Level 1
            </div>
          </button>

          {/* Card B — Jump in */}
          <button
            type="button"
            className="sp-card"
            onClick={() => navigate("/level-1")}
            style={CARD}
          >
            <div style={CARD_ICON}>🎮</div>
            <div style={CARD_LABEL}>I Know Piano</div>
            <div style={CARD_DESC}>
              Skip the tutorial and jump straight into Level 1 — play Twinkle Twinkle to make the Dragon sleep.
            </div>
            <div style={{ ...CARD_CTA, background: "#059669", color: "#fff" }}>
              Jump to Level 1 →
            </div>
            <div style={CARD_STEPS}>
              Starts immediately · No tutorial needed
            </div>
          </button>
        </div>

        {/* Footer nudge */}
        <p style={FOOTER_NOTE}>
          You can always access the tutorial later from the Help page.
        </p>

        <button
          type="button"
          onClick={() => navigate("/home")}
          style={BACK_BTN}
        >
          ← Back to home
        </button>
      </div>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────── */
const ROOT = {
  minHeight: "100vh",
  background: "linear-gradient(160deg, #e0f2fe 0%, #f0fdf4 50%, #fef9c3 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  position: "relative",
  overflow: "hidden",
  fontFamily: "'Nunito', 'Fredoka One', sans-serif",
};
const NOTES_LAYER = {
  position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0,
};
const INNER = {
  position: "relative", zIndex: 1,
  width: "100%", maxWidth: 700,
  display: "flex", flexDirection: "column", alignItems: "center",
  gap: 0,
};
const BRAND = {
  display: "flex", alignItems: "center", gap: 12, marginBottom: 28,
};
const BRAND_TITLE = {
  fontFamily: "'Fredoka One', cursive",
  fontSize: "clamp(1.1rem, 4vw, 1.4rem)",
  color: "#1e3a5f", lineHeight: 1.1,
};
const BRAND_SUB = {
  fontSize: "0.75rem", fontWeight: 700, color: "#64748b", marginTop: 2,
};
const HEADING = {
  fontFamily: "'Fredoka One', cursive",
  fontSize: "clamp(1.6rem, 5vw, 2.2rem)",
  color: "#1e293b", textAlign: "center",
  margin: 0, lineHeight: 1.2,
};
const SUB = {
  marginTop: 8, marginBottom: 32,
  fontSize: "0.95rem", fontWeight: 700, color: "#64748b", textAlign: "center",
};
const CARDS = {
  display: "flex", gap: 18, flexWrap: "wrap",
  justifyContent: "center", width: "100%",
};
const CARD = {
  flex: "1 1 280px", maxWidth: 320,
  background: "#fff",
  border: "2px solid #e2e8f0",
  borderRadius: 24,
  padding: "32px 24px 24px",
  display: "flex", flexDirection: "column", alignItems: "center",
  gap: 10, textAlign: "center",
  cursor: "pointer",
  transition: "transform .15s, box-shadow .15s, border-color .15s",
  boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
};
const CARD_ICON = {
  fontSize: "3rem", lineHeight: 1, marginBottom: 4,
};
const CARD_LABEL = {
  fontFamily: "'Fredoka One', cursive",
  fontSize: "1.35rem", color: "#1e293b", lineHeight: 1.2,
};
const CARD_DESC = {
  fontSize: "0.85rem", fontWeight: 700, color: "#64748b",
  lineHeight: 1.55, maxWidth: 260,
};
const CARD_CTA = {
  marginTop: 6,
  padding: "11px 28px",
  borderRadius: 100,
  fontFamily: "'Fredoka One', cursive",
  fontSize: "1rem",
  border: "none",
  boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
  cursor: "pointer",
  width: "100%",
};
const CARD_STEPS = {
  fontSize: "0.7rem", fontWeight: 800,
  color: "#94a3b8", letterSpacing: "0.06em",
  textTransform: "uppercase", marginTop: 2,
};
const FOOTER_NOTE = {
  marginTop: 28, fontSize: "0.78rem", fontWeight: 700,
  color: "#94a3b8", textAlign: "center",
};
const BACK_BTN = {
  marginTop: 8,
  background: "none", border: "none", cursor: "pointer",
  fontSize: "0.82rem", fontWeight: 800, color: "#94a3b8",
  textDecoration: "underline", textDecorationStyle: "dashed",
  padding: "4px 8px",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');

  .sp-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 36px rgba(0,0,0,0.13);
    border-color: #c7d2fe;
  }
  .sp-card:active {
    transform: translateY(-1px);
  }

  .sp-note {
    position: absolute;
    bottom: -8%;
    color: rgba(30,58,138,0.25);
    animation: spFloat linear infinite;
    pointer-events: none;
  }
  @keyframes spFloat {
    0%   { transform: translateY(0) rotate(-8deg); opacity: 0; }
    8%   { opacity: 0.7; }
    85%  { opacity: 0.4; }
    100% { transform: translateY(-110vh) rotate(8deg); opacity: 0; }
  }
`;